import React, { useState, useMemo, useRef } from "react";
import { Download, Upload, AlertCircle, CheckCircle2 } from "lucide-react";
import { useDispatch } from "react-redux";
import { savePayrollEntity } from "../../store/slices/payrollSlice";
import { downloadLoanTemplate, readLoanWorkbook, validateLoanRow } from "../../utils/loanImportUtils";

// Helpers to calculate due dates for loan installments
const addMonths = (date, months) => {
  const result = new Date(date);
  const originalDay = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(originalDay, lastDayOfMonth));
  return result;
};

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function ImportLoansModal({ open, onClose, employees, loanTypes, periods, getNextLoanNumber, onSuccess }) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);

    try {
      const parsed = await readLoanWorkbook(file);
      const validated = parsed.map((r) => {
        const { values, errors } = validateLoanRow(r, employees, loanTypes, periods);
        return { _row: r._row, original: r, values, errors };
      });
      setRows(validated);
    } catch (err) {
      setError("Failed to read Excel file. Please use the correct template.");
    }
    e.target.value = "";
  };

  const validRows = useMemo(() => rows.filter(r => r.errors.length === 0), [rows]);
  const invalidRows = useMemo(() => rows.filter(r => r.errors.length > 0), [rows]);

  const handleImport = async () => {
    if (!validRows.length) return;
    setImporting(true);

    try {
      // Fetch the starting loan number, and parse it to increment in memory during the loop
      const baseLoanNoStr = await getNextLoanNumber(); 
      let currentMaxId = parseInt(baseLoanNoStr.replace('LN-', ''), 10);
      let successCount = 0;

      for (const row of validRows) {
        const loanNo = `LN-${String(currentMaxId).padStart(4, "0")}`;
        const loanData = { ...row.values, loanNo, pendingSureties: [], pendingDocs: [] };

        // 1. Save the Loan
        const loanId = await dispatch(savePayrollEntity('loanManagement', loanData, false));

        // 2. Generate Installments automatically
        const firstDueDate = new Date(loanData.dueDate);
        for (let i = 1; i <= loanData.noOfInstallments; i++) {
          const installmentDueDate = formatDate(addMonths(firstDueDate, i - 1));
          await dispatch(savePayrollEntity("loanInstallments", {
            loanId: Number(loanId),
            installmentNo: i,
            payrollPeriodId: Number(loanData.startPeriodId),
            dueDate: installmentDueDate,
            installmentAmount: loanData.installmentAmount,
            paidAmount: 0,
            balanceAmount: loanData.installmentAmount,
            status: 1,
            remarks: loanData.loanDescription || "",
          }));
        }

        currentMaxId++; // Increment for next loop
        successCount++;
      }

      onSuccess(successCount);
      resetAndClose();
    } catch (err) {
      setError(err?.message || "An error occurred during import.");
    } finally {
      setImporting(false);
    }
  };

  const resetAndClose = () => {
    setRows([]);
    setError(null);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" 
        onClick={!importing ? resetAndClose : undefined}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Import Loans</h2>
          
          <div className="flex items-center gap-2">
            <button 
              type="button"
              onClick={downloadLoanTemplate}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
            >
              <Download size={16} />
              Template
            </button>
            <button 
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={16} />
              Upload File
            </button>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 text-sm rounded-md border border-red-100">
              <AlertCircle size={16} className="shrink-0" /> 
              <span>{error}</span>
            </div>
          )}

          {rows.length > 0 ? (
            <div className="flex flex-col h-full">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-medium text-gray-700">Preview Data</span>
                <div className="flex items-center gap-4 text-[13px]">
                  <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
                    <CheckCircle2 size={15} /> {validRows.length} Valid
                  </span>
                  <span className="flex items-center gap-1.5 text-red-600 font-medium">
                    <AlertCircle size={15} /> {invalidRows.length} Invalid
                  </span>
                </div>
              </div>

              <div className="border border-gray-200 rounded-md overflow-hidden flex-1 min-h-[300px]">
                <div className="overflow-y-auto max-h-[50vh]">
                  <table className="w-full text-left text-xs whitespace-nowrap">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-2.5 font-medium text-gray-600">Row</th>
                        <th className="px-4 py-2.5 font-medium text-gray-600">Service No</th>
                        <th className="px-4 py-2.5 font-medium text-gray-600">Type</th>
                        <th className="px-4 py-2.5 font-medium text-gray-600">Amount</th>
                        <th className="px-4 py-2.5 font-medium text-gray-600">Inst. Amt</th>
                        <th className="px-4 py-2.5 font-medium text-gray-600 w-1/3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rows.map((r, i) => (
                        <tr key={i} className={r.errors.length ? "bg-red-50/50" : "hover:bg-gray-50/50"}>
                          <td className="px-4 py-2 text-gray-500">{r._row}</td>
                          <td className="px-4 py-2 font-medium text-gray-900">{r.original.serviceNo}</td>
                          <td className="px-4 py-2 text-gray-700">{r.original.loanTypeName}</td>
                          <td className="px-4 py-2 text-gray-700">
                            {r.values.loanAmount != null 
                              ? r.values.loanAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
                              : '-'}
                          </td>
                          <td className="px-4 py-2 text-gray-700">
                            {r.values.installmentAmount != null 
                              ? r.values.installmentAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
                              : '-'}
                          </td>
                          <td className="px-4 py-2">
                            {r.errors.length === 0 ? (
                              <span className="inline-flex items-center text-emerald-600 font-medium">Ready</span>
                            ) : (
                              <span className="inline-flex items-center text-red-600 whitespace-normal min-w-[200px]">{r.errors.join(', ')}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-12 w-12 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                <Upload className="text-gray-400" size={24} />
              </div>
              <p className="text-sm font-medium text-gray-900">No data to display</p>
              <p className="text-sm text-gray-500 mt-1">Please upload an Excel file to see the preview.</p>
            </div>
          )}
        </div>

        {/* Actions / Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 shrink-0 rounded-b-xl">
          <button 
            type="button"
            onClick={resetAndClose} 
            disabled={importing} 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={importing || validRows.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
          >
            {importing ? "Importing..." : `Import ${validRows.length} valid row(s)`}
          </button>
        </div>
        
      </div>
    </div>
  );
}